import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  Calendar,
  Plus,
  RefreshCw,
  Play,
  CheckCircle,
  DollarSign,
  Filter
} from 'lucide-react';
import { toast } from 'react-toastify';
import {
  getPayrollRuns,
  createPayrollRun,
  processPayrollRun,
  approvePayrollRun,
  markPayrollPaid
} from '../../api/payroll';
import PeriodFilter from '../../components/common/PeriodFilter';
import BlurredValue from '../../components/common/BlurredValue';
import { useFiscalPeriod } from '../../contexts/FiscalPeriodContext';
import { useAuth } from '../../contexts/AuthContext';
import { getBranches } from '../../api/branches';

const PayrollRunsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { periodRange, getDateRangeParams } = useFiscalPeriod();
  const isSuperAdmin = user?.role === 'super_admin' || user?.role === 'system_admin';

  const [runs, setRuns] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showPayModal, setShowPayModal] = useState(null);
  const [paymentRef, setPaymentRef] = useState('');

  const [newRun, setNewRun] = useState({
    pay_period_start: '',
    pay_period_end: '',
    payment_date: '',
    branch_id: '',
    description: ''
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { startDate, endDate } = getDateRangeParams();
      const [runsResponse, branchesResponse] = await Promise.all([
        getPayrollRuns({ status: statusFilter, module: 'retail', startDate, endDate }),
        getBranches()
      ]);
      // Extract data arrays from API responses
      const runsArray = Array.isArray(runsResponse)
        ? runsResponse
        : runsResponse?.data || runsResponse?.runs || [];
      const branchesArray = Array.isArray(branchesResponse)
        ? branchesResponse
        : branchesResponse?.branches || branchesResponse?.data || [];
      setRuns(runsArray);
      setBranches(branchesArray);
    } catch (error) {
      toast.error('Failed to load payroll runs');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, getDateRangeParams]);

  useEffect(() => {
    fetchData();
  }, [fetchData, periodRange]);

  // Handle auto-open create modal from navigation state
  useEffect(() => {
    if (location.state?.openCreateModal) {
      setShowCreateModal(true);
      // Clear state to prevent reopening on generic refresh (optional but good practice)
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleCreateRun = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const run = await createPayrollRun(newRun);
      toast.success('Payroll run created');
      setShowCreateModal(false);
      setNewRun({
        pay_period_start: '',
        pay_period_end: '',
        payment_date: '',
        branch_id: '',
        description: ''
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to create payroll run');
    } finally {
      setCreating(false);
    }
  };

  const handleProcess = async (runId) => {
    if (
      !confirm('Process this payroll run? This will generate payslips for all eligible employees.')
    )
      return;
    try {
      await processPayrollRun(runId);
      toast.success('Payroll processed successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to process payroll');
    }
  };

  const handleApprove = async (runId) => {
    if (!confirm('Approve this payroll run?')) return;
    try {
      await approvePayrollRun(runId);
      toast.success('Payroll approved');
      fetchData();
    } catch (error) {
      toast.error('Failed to approve payroll');
    }
  };

  const handleMarkPaid = async (runId) => {
    try {
      await markPayrollPaid(runId, paymentRef);
      toast.success('Payroll marked as paid');
      setShowPayModal(null);
      setPaymentRef('');
      fetchData();
    } catch (error) {
      toast.error('Failed to mark as paid');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-700',
      processing: 'bg-blue-100 text-blue-700',
      pending_approval: 'bg-yellow-100 text-yellow-700',
      approved: 'bg-green-100 text-green-700',
      paid: 'bg-emerald-100 text-emerald-700',
      cancelled: 'bg-red-100 text-red-700'
    };
    return styles[status] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="min-h-screen bg-secondary-offWhite p-3 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/payroll')}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-primary-black">Payroll Runs</h1>
                <p className="text-sm text-gray-500">Process and manage monthly payroll</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <PeriodFilter storageKey="payroll_runs" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border rounded-lg"
              >
                <option value="">All Status</option>
                <option value="draft">Draft</option>
                <option value="pending_approval">Pending Approval</option>
                <option value="approved">Approved</option>
                <option value="paid">Paid</option>
              </select>
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-accent-gold text-primary-black font-semibold rounded-lg hover:bg-accent-goldDark"
              >
                <Plus className="w-4 h-4" />
                New Run
              </button>
              <button onClick={fetchData} className="p-2 border rounded-lg hover:bg-gray-50">
                <RefreshCw className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Runs Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin w-8 h-8 border-4 border-accent-gold border-t-transparent rounded-full mx-auto"></div>
            </div>
          ) : runs.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Description</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Period</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Branch</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold">Employees</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold">Net Pay</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {runs.map((run) => (
                    <tr
                      key={run.id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => navigate(`/payroll/runs/${run.id}`)}
                    >
                      <td className="px-4 py-3 font-medium">
                        {run.description || `Run #${run.id}`}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {new Date(run.pay_period_start).toLocaleDateString()} -{' '}
                        {new Date(run.pay_period_end).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-sm">{run.branch_name || 'All'}</td>
                      <td className="px-4 py-3 text-center">{run.employee_count || 0}</td>
                      <td className="px-4 py-3 text-right font-semibold">
                        <BlurredValue value={formatCurrency(run.total_net)} />
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(run.status)}`}
                        >
                          {run.status.replace('_', ' ')}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-8 text-center text-gray-500">
              <Calendar className="w-12 h-12 mx-auto mb-2 text-gray-300" />
              <p>No payroll runs found</p>
            </div>
          )}
        </div>

        {/* Create Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
              <div className="p-4 border-b flex items-center justify-between">
                <h2 className="text-lg font-semibold">Create Payroll Run</h2>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  ×
                </button>
              </div>
              <form onSubmit={handleCreateRun} className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Pay Period Start *</label>
                  <input
                    type="date"
                    value={newRun.pay_period_start}
                    onChange={(e) => setNewRun({ ...newRun, pay_period_start: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Pay Period End *</label>
                  <input
                    type="date"
                    value={newRun.pay_period_end}
                    onChange={(e) => setNewRun({ ...newRun, pay_period_end: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Payment Date</label>
                  <input
                    type="date"
                    value={newRun.payment_date}
                    onChange={(e) => setNewRun({ ...newRun, payment_date: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Branch (Optional)</label>
                  <select
                    value={newRun.branch_id}
                    onChange={(e) => setNewRun({ ...newRun, branch_id: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="">All Branches</option>
                    {branches.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Description</label>
                  <input
                    type="text"
                    value={newRun.description}
                    onChange={(e) => setNewRun({ ...newRun, description: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="e.g., December 2024 Payroll"
                  />
                </div>
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2 border rounded-lg"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={creating}
                    className="px-4 py-2 bg-accent-gold text-primary-black font-semibold rounded-lg disabled:opacity-50"
                  >
                    {creating ? 'Creating...' : 'Create Run'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Pay Modal */}
        {showPayModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-sm">
              <div className="p-4 border-b">
                <h2 className="text-lg font-semibold">Mark as Paid</h2>
              </div>
              <div className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Payment Reference</label>
                  <input
                    type="text"
                    value={paymentRef}
                    onChange={(e) => setPaymentRef(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="e.g., TRF-123456"
                  />
                </div>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setShowPayModal(null)}
                    className="px-4 py-2 border rounded-lg"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleMarkPaid(showPayModal)}
                    className="px-4 py-2 bg-emerald-600 text-white rounded-lg"
                  >
                    Confirm Payment
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PayrollRunsPage;
